import React, { ReactNode, useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import clsx from 'clsx';
import { Button } from 'antd';
import { VisitorHelpStatus, SESSION_INTERACTION_KIND } from 'sb-lib/types';
import {
  AppNotification,
  AppNotificationType,
  AppNotificationVisitorCall,
  isAppNotificationVisitorAlert,
  isAppNotificationVisitorCall,
  isAppNotificationVisitorContactCreated,
  isAppNotificationVisitorMeetingBooked,
  isAppNotificationWithVisitor,
  isAppNotificationVisitorConversationUpdate,
  isAppNotificationCompanyCreated,
  isAppNotificationSixsenseFetchesCapExceeded,
} from 'types';
import { Icon } from 'shared/components/Icon';
import { distanceToNowShort } from 'shared/utils/time-formatting';
import { durationFrom } from 'shared/utils/helpers';
import { useAppDispatch, useAppSelector } from 'shared/store';
import { selectRealtimeVisitor } from 'shared/store/realtime-slice';
import { selectActiveUser } from 'shared/store/shared-selectors';
import { useInterval, useUpdatingRef } from 'shared/hooks';
import {
  appNotificationExpired,
  readAppNotification,
  uiWantsModalOpenChanged,
} from 'shared/store/app-notification-slice';
import { makeLog } from 'shared/utils/log';
import './style.less';
import { activateVisitor, startCall } from 'shared/store/dashboard-slice';
import { getVisitorConversation } from 'shared/api';

const NAME = 'AppNotificationItem';
const log = makeLog(NAME);
export const AppNotificationItem: React.FC<{
  notification: AppNotification;
  className?: string;
}> = ({ className, notification }) => {
  const dispatch = useAppDispatch();

  // Take care here to not cause redux perf-issues
  // Use an updating ref to ensure fresh data for expired popup flag
  const popupExpRef = useUpdatingRef(notification.isPopupExpired);
  useInterval(() => {
    if (popupExpRef.current) return;
    if (notification.popupExpiration && Date.now() > notification.popupExpiration) {
      dispatch(appNotificationExpired(notification.id));
    }
  }, 1000);

  // Find the correct comonent via the notification type
  const config = ANConfigByType[notification.notificationType];
  if (!config) {
    log.warn(`No config defined for [${notification.notificationType}]`, notification);
    return <div>No config found for type: {notification.notificationType}</div>;
  }
  return <config.Component an={notification} className={className} />;
};

/**************************************************************
 *  Component parts to compose specific notification types
 **************************************************************/
type ANProp = { an: AppNotification; className?: string };
type ANComponent = React.FC<ANProp>;
type ANMainProps = ANProp & {
  children: ReactNode;
  // See ./style.less .all-themes() mixin
  theme: 'orange' | 'red' | 'purple';
  className?: string;
  expired?: boolean;
  unread?: boolean;
  onClick?: (ev?: React.MouseEvent) => void;
};
const ANMain: React.FC<ANMainProps> = ({
  an,
  children,
  theme,
  className,
  expired,
  unread,
  onClick,
}) => {
  const exp = an.popupExpiration;
  const isExp = exp ? Date.now() > exp : false;

  const expiredClass = expired !== undefined ? expired : isExp;
  const unreadClass = unread !== undefined ? unread : !an.dateRead;
  const clickableClass = !!onClick;

  return (
    <div
      className={clsx(
        NAME,
        `${NAME}--nt-${an.notificationType}`,
        `${NAME}--theme-${theme}`,
        expiredClass && `${NAME}--expired`,
        unreadClass && `${NAME}--unread`,
        clickableClass && `${NAME}--clickable`,
        className,
      )}
      onClick={onClick}
    >
      {children}
    </div>
  );
};
const ANAccent: ANComponent = () => (
  <div className={`${NAME}__accent`}>
    <div>&nbsp;</div>
  </div>
);
const ANIconCol: React.FC<ANProp & { count?: number; icon: ReactNode }> = ({
  icon,
  count,
}) => {
  const countDisplay = !count ? '' : count > 9 ? '9+' : count.toString();
  return (
    <div className={`${NAME}__icon-col`}>
      <div className={`${NAME}__icon`}>
        {count && <div>{countDisplay}</div>}
        {icon}
      </div>
    </div>
  );
};
const ANEndCol: React.FC<ANProp & { humanized?: boolean }> = ({ an, humanized }) => {
  const createdTime = Date.parse(an.dateCreated);
  const [time, setTime] = useState(
    humanized ? distanceToNowShort(createdTime, ' ago') : durationFrom(createdTime),
  );
  useEffect(() => {
    const interval = setInterval(() => {
      setTime(
        humanized ? distanceToNowShort(createdTime, ' ago') : durationFrom(createdTime),
      );
    }, 1000);
    return () => clearTimeout(interval);
  }, [createdTime, humanized]);

  return (
    <div className={`${NAME}__end-col`}>
      <div className={`${NAME}__time`}>{time}</div>
      <div className={`${NAME}__status-dot`}>&nbsp;</div>
    </div>
  );
};
const ANVisitorDescription: ANComponent = ({ an }) => {
  if (!isAppNotificationWithVisitor(an)) return null;

  const location = an.visitor.lastLocation || SERVER_NO_LOC;
  const name = an.contact?.name || 'Unknown';
  const noLoc = SERVER_NO_LOC === location;
  const noCo = !an.company?.name;

  return (
    <>
      {name}
      {!noCo ? ' at ' : !noLoc ? ' from ' : ''}
      {!noCo ? an.company?.name : !noLoc ? location : ''}
    </>
  );
};

const isExpired = (an: AppNotification) => {
  const exp = an.popupExpiration;
  return exp ? Date.now() > exp : false;
};

type HandleGoFn = (
  an: AppNotification,
  dispatch: ReturnType<typeof useAppDispatch>,
  history: ReturnType<typeof useHistory>,
  isCall?: boolean,
  noCloseNotifications?: boolean,
) => void;
const handleGoVisitor: HandleGoFn = (
  an,
  dispatch,
  history,
  isCall,
  noCloseNotifications,
) => {
  if (isAppNotificationWithVisitor(an)) {
    if (!noCloseNotifications) {
      dispatch(uiWantsModalOpenChanged(false));
    }

    if (!isCall) {
      history.push(`/visitor/${an.visitor.id}`);
      return;
    }

    dispatch(readAppNotification(an.id));
    getVisitorConversation(an.visitor.id).then(({ data }) => {
      dispatch(activateVisitor({ visitorId: an.visitor.id }));
      dispatch(startCall({ activeConversationId: data.id }));
    });
  } else {
    log.warn(
      `Tried to go to visitor from notification w/o visitor ${an.notificationType}`,
    );
  }
};

// Server sends this string if location unknown
const SERVER_NO_LOC = 'Unknown location';

/******************************
 *  VisitorAlert
 ******************************/

const ANVisitorAlert: ANComponent = ({ an, className }) => {
  if (!isAppNotificationVisitorAlert(an)) {
    throw Error(`Incorrect app notification type [${an.notificationType}]`);
  }
  const history = useHistory();
  const dispatch = useAppDispatch();
  const isExp = isExpired(an);
  const goVisitor = () => handleGoVisitor(an, dispatch, history);
  return (
    <ANMain
      className={className}
      an={an}
      theme={'red'}
      onClick={isExp ? goVisitor : undefined}
    >
      {!isExp && <ANAccent an={an} />}
      <ANIconCol an={an} icon={<Icon icon="bell" />} />
      <div className={`${NAME}__text-col`}>
        <div className={`${NAME}__title`}>
          <ANVisitorDescription an={an} />
        </div>
        <div className={`${NAME}__text`}>
          <b>{an.data.title}</b> {an.data.body ? <> - {an.data.body}</> : <></>}
        </div>
        {!isExp && (
          <div className={`${NAME}__actions`}>
            <Button
              type="ghost"
              className={clsx(`${NAME}__action`, `${NAME}__action--primary`)}
              icon={<Icon icon="callOutgoing" />}
              onClick={() => handleGoVisitor(an, dispatch, history, true)}
            >
              Start call
            </Button>
            <Button
              type="ghost"
              className={clsx(`${NAME}__action`, `${NAME}__action--secondary`)}
              onClick={goVisitor}
            >
              View session
            </Button>
          </div>
        )}
      </div>
      <ANEndCol an={an} humanized={isExp} />
    </ANMain>
  );
};

/******************************
 *  VisitorCall
 ******************************/
const getCallAnswerInteraction = (an: AppNotificationVisitorCall) => {
  if (an.session) {
    return an.session.sessionInteractions.find(
      si =>
        si.kind === SESSION_INTERACTION_KIND.VIDEO &&
        si.startTime > Date.parse(an.dateCreated),
    );
  }
};
const ANVisitorCall: ANComponent = ({ an, className }) => {
  if (!isAppNotificationVisitorCall(an)) {
    throw Error(`Incorrect app notification type [${an.notificationType}]`);
  }

  const history = useHistory();
  const dispatch = useAppDispatch();
  const agent = useAppSelector(selectActiveUser);
  const realtimeVisitor = useAppSelector(s => selectRealtimeVisitor(s, an.visitor.id));
  const isExp = isExpired(an);
  const goVisitor = () => handleGoVisitor(an, dispatch, history);
  const routedToMe = !!agent && an.data.routedUserIds.includes(agent.id);
  const isCalling =
    realtimeVisitor && realtimeVisitor.helpStatus === VisitorHelpStatus.WAITING;
  const answer = getCallAnswerInteraction(an);
  const answerBy =
    (answer && (answer.user.id === agent?.id ? 'you' : answer.user.displayName)) || '';

  return (
    <ANMain
      className={className}
      an={an}
      theme={'orange'}
      onClick={isExp ? goVisitor : undefined}
    >
      {isExp ? (
        <>
          <ANIconCol an={an} icon={<Icon icon="call" />} />
          <div className={`${NAME}__text-col`}>
            <div className={`${NAME}__text`}>
              Visitor called {routedToMe ? 'you' : 'org'}
              {answerBy && <>, answered by {answerBy} </>}
            </div>
            <div className={`${NAME}__title`}>
              <ANVisitorDescription an={an} />
            </div>
          </div>
          <ANEndCol an={an} humanized />
        </>
      ) : (
        <>
          <ANAccent an={an} />
          <ANIconCol an={an} icon={<Icon icon="call" />} />
          <div className={`${NAME}__text-col`}>
            <div className={`${NAME}__text`}>
              {answer ? (
                <>Call answered</>
              ) : isCalling ? (
                <>Visitor is calling</>
              ) : (
                <>Visitor called</>
              )}
            </div>
            <div className={`${NAME}__title`}>
              <ANVisitorDescription an={an} />
            </div>
            <div className={`${NAME}__actions`}>
              <Button
                type="ghost"
                className={clsx(`${NAME}__action`, `${NAME}__action--primary`)}
                icon={<Icon icon={routedToMe ? 'checkmark' : 'callOutgoing'} />}
                onClick={() => handleGoVisitor(an, dispatch, history, true)}
                disabled={!realtimeVisitor}
              >
                {routedToMe && !!realtimeVisitor ? 'Accept call' : 'Start call'}
              </Button>
              <Button
                type="ghost"
                className={clsx(`${NAME}__action`, `${NAME}__action--secondary`)}
                onClick={goVisitor}
              >
                View session
              </Button>
            </div>
          </div>
          <ANEndCol an={an} />
        </>
      )}
    </ANMain>
  );
};

/******************************
 *  VisitorMeetingBooked
 ******************************/

const ANVisitorMeetingBooked: ANComponent = ({ an, className }) => {
  if (!isAppNotificationVisitorMeetingBooked(an)) {
    throw Error(`Incorrect app notification type [${an.notificationType}]`);
  }
  const dispatch = useAppDispatch();
  const history = useHistory();
  const who = an.calendarEvent.locationInfo.name || an.calendarEvent.emailAddress;
  const isExp = isExpired(an);
  const handleClick = () => {
    dispatch(uiWantsModalOpenChanged(false));
    dispatch(readAppNotification(an.id));
    // no deep-links supported yet, just go to list
    history.push(`/scheduling/scheduled-meetings`);
  };
  return (
    <ANMain className={className} an={an} theme={'orange'} onClick={handleClick}>
      {!isExp && <ANAccent an={an} />}
      <ANIconCol an={an} icon={<Icon icon="calendar" />} />
      <div className={`${NAME}__text-col`}>
        <div className={`${NAME}__text`}>New meeting booked</div>
        <div className={`${NAME}__title`}>{who}</div>
      </div>
      <ANEndCol an={an} humanized={isExp} />
    </ANMain>
  );
};

/******************************
 *  VisitorContactCreated
 ******************************/

const ANVisitorContactCreated: ANComponent = ({ an, className }) => {
  if (!isAppNotificationVisitorContactCreated(an)) {
    throw Error(`Incorrect app notification type [${an.notificationType}]`);
  }
  const dispatch = useAppDispatch();
  const history = useHistory();
  const isExp = isExpired(an);
  const handleClick = () => {
    dispatch(uiWantsModalOpenChanged(false));
    dispatch(readAppNotification(an.id));
    history.push(`/visitor/${an.contact.originalVisitorId}?overview`);
  };
  return (
    <ANMain className={className} an={an} theme={'orange'} onClick={handleClick}>
      {!isExp && <ANAccent an={an} />}
      <ANIconCol an={an} icon={<Icon icon="email" />} />
      <div className={`${NAME}__text-col`}>
        <div className={`${NAME}__text`}>New email submitted</div>
        <div className={`${NAME}__title`}>{an.contact.email}</div>
      </div>
      <ANEndCol an={an} humanized={isExp} />
    </ANMain>
  );
};

/******************************
 *  VisitorConversationUpdate
 ******************************/

const ANVisitorConversationUpdate: ANComponent = ({ an, className }) => {
  if (!isAppNotificationVisitorConversationUpdate(an)) {
    throw Error(`Incorrect app notification type [${an.notificationType}]`);
  }
  const dispatch = useAppDispatch();
  const history = useHistory();
  const handleClick = () => {
    dispatch(uiWantsModalOpenChanged(false));
    dispatch(readAppNotification(an.id));
    history.push(`/visitor/${an.visitor.id}?cid=${an.conversation.id}`);
  };
  const title = an.contact?.name || an.contact?.email || 'Unknown';
  const visUnread = (an.conversation && an.conversation.unreadMessageCounts.visitor) || 0;
  const text = [0, 1].includes(visUnread)
    ? an.conversation?.lastMessage?.text || '(no message text)'
    : `${visUnread} new messages`;
  return (
    <ANMain
      expired={true}
      className={className}
      an={an}
      theme={'purple'}
      onClick={handleClick}
    >
      <ANIconCol an={an} count={visUnread} icon={<Icon icon="bubble" />} />
      <div className={`${NAME}__text-col`}>
        <div className={`${NAME}__title`}>{title}</div>
        <div className={`${NAME}__text`}>{text}</div>
      </div>
      <ANEndCol an={an} humanized />
    </ANMain>
  );
};

/******************************
 *  CompanyCreated
 ******************************/

const ANCompanyCreated: ANComponent = ({ an, className }) => {
  if (!isAppNotificationCompanyCreated(an)) {
    throw Error(`Incorrect app notification type [${an.notificationType}]`);
  }
  const [logoFail, setLogoFail] = useState(!an.company.domain);
  const dispatch = useAppDispatch();
  const history = useHistory();
  const isExp = isExpired(an);
  const handleClick = () => {
    dispatch(uiWantsModalOpenChanged(false));
    dispatch(readAppNotification(an.id));

    history.push(`/spotlight/account/${an.company.id}`);
  };

  const company = an.company.name || an.company.domain || 'Unnamed';
  const visitor = an.contact?.name || an.contact?.email || '';
  const text = visitor ? (
    <>
      {company} <span>({visitor})</span>
    </>
  ) : (
    `Someone from ${company}`
  );

  return (
    <ANMain className={className} an={an} theme={'orange'} onClick={handleClick}>
      {!isExp && <ANAccent an={an} />}
      <ANIconCol
        an={an}
        icon={
          !logoFail ? (
            <img
              src={`https://logo.clearbit.com/${an.company.domain}`}
              loading="lazy"
              onError={() => setLogoFail(true)}
            />
          ) : (
            <Icon icon="company" />
          )
        }
      />
      <div className={`${NAME}__text-col`}>
        <div className={`${NAME}__text`}>New company visited</div>
        <div className={`${NAME}__title`}>{text}</div>
      </div>
      <ANEndCol an={an} humanized={isExp} />
    </ANMain>
  );
};

/******************************
 * SixsenseFetchesCapExceeded
 ******************************/

const ANSixsenseFetchesCapExceeded: ANComponent = ({ an, className }) => {
  if (!isAppNotificationSixsenseFetchesCapExceeded(an)) {
    throw Error(`Incorrect app notification type [${an.notificationType}]`);
  }
  const history = useHistory();
  const isExp = isExpired(an);
  const handleClick = () => {
    history.push(`/settings/integrations`);
  };

  return (
    <ANMain className={className} an={an} theme={'red'} onClick={handleClick}>
      {!isExp && <ANAccent an={an} />}
      <ANIconCol an={an} icon={<Icon icon="warn" />} />
      <div className={`${NAME}__text-col`}>
        <div className={`${NAME}__text`}>6sense Credits Exhausted</div>
        <div className={`${NAME}__title`}>
          Your org plan 6sense credits have been exhausted. You may increase the plan
          limit or use your own 6sense api key. Please ring ServiceBell for more
          information.
        </div>
      </div>
      <ANEndCol an={an} humanized={isExp} />
    </ANMain>
  );
};

/****************************
 *     Configurations
 * ★ compose when possible ★
 ****************************/

type ANConfig = {
  Component: ANComponent;
};

const ANConfigByType: Record<AppNotificationType, ANConfig> = {
  [AppNotificationType.visitor_alert]: {
    Component: ANVisitorAlert,
  },
  [AppNotificationType.visitor_call]: {
    Component: ANVisitorCall,
  },
  [AppNotificationType.visitor_meeting_booked]: {
    Component: ANVisitorMeetingBooked,
  },
  [AppNotificationType.visitor_contact_created]: {
    Component: ANVisitorContactCreated,
  },
  [AppNotificationType.visitor_conversation_update]: {
    Component: ANVisitorConversationUpdate,
  },
  [AppNotificationType.company_created]: {
    Component: ANCompanyCreated,
  },
  [AppNotificationType.sixsense_fetches_cap_exceeded]: {
    Component: ANSixsenseFetchesCapExceeded,
  },
};
